// Le mode chronométré (`mc`) ne concerne que les mots FRAGILES (force 0-1) : on
// laisse largement le temps de récupérer un mot récent sans pression. Cette même
// durée sert aussi de fenêtre d'étude après une mauvaise réponse (mot correct +
// exemple avant l'avance auto), que « suivant → » permet toujours d'écourter.
export const TIMER_SECONDS = 25;
export const PAUSE_THRESHOLD = 3;

// Durée du feedback avant l'avance auto, PARTAGÉE par le QCM (QuizGame) et les
// exercices de production (BonusGame). Elle ne dépend PAS du résultat — bonne et
// mauvaise réponse durent pareil — mais de ce qu'il y a à lire : une carte qui
// montre une phrase d'exemple laisse le temps de l'écouter et de la comprendre.
// Les exercices avaient leurs propres constantes (700 ms sur un succès) : la
// traduction et l'audio du texte à trou passaient en un éclair, et le rythme
// changeait d'un type d'exercice à l'autre sans raison.
export const ADVANCE_MS_EXAMPLE = 5000;
export const ADVANCE_MS_PLAIN   = 2600;

// Plusieurs mots russes partagent une même traduction, parfois à la casse près
// (Послать « Envoyer » et Посылать « envoyer »), parfois à l'identique
// (Мороженое et Лёд → « Glace »).
//
// Mais le cas le plus vicieux n'est pas l'égalité : c'est le RECOUVREMENT PARTIEL.
// Nos traductions sont des listes de sens (« Boire » face à « Boire, prendre un
// verre »). Comparées comme des chaînes entières, elles sont différentes, donc les
// deux se retrouvaient proposées ensemble — et l'utilisateur qui choisissait la
// bonne réponse par le mauvais bouton était compté FAUX. Signalé sur le thème
// « nourriture », où ces doublons de sens abondent.
//
// On compare donc SENS PAR SENS : on découpe sur les virgules et points-virgules,
// on normalise (minuscules, accents retirés, parenthèses retirées), et deux
// traductions sont en conflit dès qu'elles partagent un sens.
const normalizeSense = (s) =>
  (s ?? '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')   // accents
    .replace(/\([^)]*\)/g, ' ')                       // « où (avec mouvement) »
    .toLowerCase()
    .replace(/[^a-z0-9' ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const sensesOf = (value) =>
  new Set(
    (value ?? '')
      .split(/[,;/]/)
      .map(normalizeSense)
      .filter(Boolean)
  );

const sharesSense = (a, b) => {
  for (const s of a) if (b.has(s)) return true;
  return false;
};

// Deux traductions brutes partagent-elles un sens ? (version publique, pour le
// garde-fou anti-répétition de QuizGame.)
export const shareSense = (a, b) => {
  if (!a || !b) return false;
  return sharesSense(sensesOf(a), sensesOf(b));
};

// Distracteurs jouables : traduction non vide, sans AUCUN sens en commun avec la
// bonne réponse, ni avec un distracteur déjà retenu (sinon deux boutons disent la
// même chose et l'un des deux est faux par accident).
export const distractorPool = (words, currentWord, wordField) => {
  const taken = [sensesOf(currentWord[wordField])];
  const pool = [];
  for (const w of words) {
    if (w.id === currentWord.id) continue;
    const senses = sensesOf(w[wordField]);
    if (!senses.size) continue;
    if (taken.some((t) => sharesSense(senses, t))) continue;
    taken.push(senses);
    pool.push(w);
  }
  return pool;
};

const shuffled = (arr) => arr.slice().sort(() => Math.random() - 0.5);

// Tire `n` leurres jouables pour `word`. À utiliser par TOUS les exercices à choix,
// y compris ceux dont les boutons portent le mot RUSSE (sens inverse, texte à trou) :
// le piège y est le même, simplement retourné. « Boire » proposait « Пить » ET
// « Выпить », deux réponses correctes dont une comptée fausse. Le conflit se juge
// toujours sur le SENS (la traduction), jamais sur la forme affichée.
export const pickDistractors = (word, pool, wordField, n) =>
  shuffled(distractorPool(pool || [], word, wordField)).slice(0, n);

// Leurres pour le TEXTE À TROU. Le piège y est différent des autres exercices :
// le conflit ne vient pas du sens du mot, mais de ce qui « rentre » dans la phrase.
// Signalé sur top 200 ∩ nourriture : la phrase acceptait eau, nourriture ET viande —
// trois réponses correctes, deux comptées fausses par pure convention.
//
// On ne peut pas savoir sans analyse syntaxique ce qui complète une phrase. Mais on
// dispose du champ lexical (`themes`) : un leurre pris dans un AUTRE champ que le mot
// cible est presque toujours implausible (« Je bois du thé avec de la MUSIQUE »).
// D'où la règle : leurres du texte à trou = sens disjoint ET thème disjoint.
const themesOf = (w) =>
  new Set(String(w?.themes || '').split(',').map((t) => t.trim()).filter(Boolean));

// Les quatre classes GRAMMATICALES de la taxonomie (les 36 autres sont sémantiques).
// Un mot-outil ne peut pas servir de texte à trou : « Я живу ___ Париже » accepte
// в, на, у — et « ___ он пришёл » accepte à peu près n'importe quelle conjonction.
// Aucun filtre par champ lexical n'y peut rien, puisque ces mots n'ont pas de champ
// lexical : c'est la syntaxe qui décide, et on ne l'analyse pas.
const GRAMMATICAL = new Set(['prepositions', 'pronoms-questions', 'liens-logiques', 'verbes-essentiels']);
const isFunctionWord = (w) => {
  for (const t of themesOf(w)) if (GRAMMATICAL.has(t)) return true;
  return false;
};

// Un mot peut-il servir de cible à un texte à trou ?
export const clozeAllowed = (word) => !isFunctionWord(word);

export const clozeDistractors = (word, pool, wordField, n) => {
  if (!clozeAllowed(word)) return [];
  const cible = themesOf(word);
  const disjoint = distractorPool(pool || [], word, wordField).filter((w) => {
    // Un mot-outil comme leurre rendrait la question triviale (on voit tout de suite
    // qu'une préposition ne remplit pas un trou de nom).
    if (isFunctionWord(w)) return false;
    const t = themesOf(w);
    if (!cible.size || !t.size) return true; // thème inconnu : on ne peut rien affirmer
    for (const x of t) if (cible.has(x)) return false;
    return true;
  });
  return shuffled(disjoint).slice(0, n);
};

export const generateOptions = (words, currentWord, optionCount, showNone, noneCorrect, wordField, noneLabel) => {
  const correctOption = currentWord[wordField];
  const pool = shuffled(distractorPool(words, currentWord, wordField));

  if (showNone && noneCorrect) {
    return shuffled([
      ...pool.slice(0, optionCount - 1).map((w) => w[wordField]),
      noneLabel,
    ]);
  }

  if (showNone && !noneCorrect) {
    return shuffled([
      correctOption,
      ...pool.slice(0, optionCount - 2).map((w) => w[wordField]),
      noneLabel,
    ]);
  }

  return shuffled([
    correctOption,
    ...pool.slice(0, optionCount - 1).map((w) => w[wordField]),
  ]);
};

export const getButtonClass = (option, isAnswered, noneCorrect, currentWord, selectedOption, noneLabel, wordField) => {
  if (!isAnswered) {
    return 'bg-slate-700 hover:bg-blue-600 text-white border border-slate-600 hover:border-blue-500';
  }
  const isCorrect = noneCorrect ? option === noneLabel : option === currentWord[wordField];
  if (isCorrect) return GOOD_OPTION;
  if (option === selectedOption) return BAD_OPTION;
  return DIMMED_OPTION;
};

// Verdict d'une option. Des aplats pleins (bg-green-600 / bg-red-700) étaient les
// deux surfaces les plus saturées de tout le site : sur une carte devenue sobre,
// ils hurlaient. Un fond teinté + une bordure + un texte colorés disent la même
// chose — vert = juste, rouge = faux — sans casser la palette slate.
export const GOOD_OPTION = 'bg-green-500/15 text-green-300 border border-green-500/60';
export const BAD_OPTION  = 'bg-red-500/15 text-red-300 border border-red-500/60';

// Leurre après la réponse. Il s'efface presque, mais reste : les faire DISPARAÎTRE
// laissait un ou deux boutons isolés au milieu de trous, très visible sur téléphone.
// Assez éteint pour ne plus attirer l'œil, assez présent pour que la grille tienne.
// Partagé avec les exercices de production (BonusGame.optClass).
export const DIMMED_OPTION = 'bg-slate-800/50 text-slate-600 border border-slate-700/40';

export const calculateSuccessRate = (correctAnswers, totalAnswered) => {
  return totalAnswered === 0 ? 0 : (correctAnswers / totalAnswered) * 100;
};

// Le pool de distracteurs peut être plus petit que le nombre d'options visé :
// on borne par intervalle plutôt que par égalité stricte.
export const getGridClass = (optionCount) => {
  if (optionCount <= 4) return 'grid grid-cols-2 gap-3 md:gap-4';
  if (optionCount <= 6) return 'grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4';
  return 'grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4';
};
